import React, { useState, useEffect } from 'react';
import { shopsApi, cashiersApi } from '../utils/api';
import { Typography, Button, Input, Table, Switch, Alert, Card, Form, Space, Tag, Select, Spin } from 'antd';
import { PlusOutlined, CheckOutlined, CloseOutlined, ShopOutlined } from '@ant-design/icons';
import { Popconfirm } from 'antd';

const { Title } = Typography;
const { Option } = Select;

const Cashiers = () => {
  const [cashiers, setCashiers] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingShops, setLoadingShops] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCashiers();
    fetchShops();
  }, []);

  const fetchCashiers = async () => {
    try {
      setLoading(true);
      const response = await cashiersApi.getAll();
      setCashiers(response.data || []);
      setError(null);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Impossible de récupérer la liste des caissiers.';
      setError(errorMsg);
      console.error('Erreur lors de la récupération des caissiers:', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchShops = async () => {
    try {
      setLoadingShops(true);
      const response = await shopsApi.getAll({ is_active: true });
      setShops(response.data || []);
    } catch (err) {
      console.error('Erreur lors de la récupération des boutiques:', err.response?.data || err.message);
    } finally {
      setLoadingShops(false);
    }
  };

  const handleAddCashier = async (values) => {
    try {
      setLoading(true);
      await cashiersApi.create({
        name: values.name,
        pinCode: values.pinCode,
        shopId: values.shopId
      });
      setSuccess('Caissier ajouté avec succès');
      setError(null);
      form.resetFields();
      fetchCashiers();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Erreur lors de l\'ajout du caissier';
      setError(errorMsg);
      setSuccess(null);
      console.error('Erreur lors de l\'ajout du caissier:', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id, isActive) => {
    try {
      setLoading(true);
      console.log(`Tentative de mise à jour du caissier ID: ${id} vers is_active: ${!isActive}`);
      var data = await cashiersApi.update(id, { is_active: !isActive });
      console.log(data);
      setSuccess(`Caissier ${isActive ? 'désactivé' : 'activé'} avec succès`);
      setError(null);
      fetchCashiers();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Erreur lors de la mise à jour du statut du caissier';
      setError(errorMsg);
      setSuccess(null);
      console.error('Erreur lors de la mise à jour du caissier:', err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const columns = [
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Boutique',
      dataIndex: 'shop_name',
      key: 'shop_name',
      render: (shopName, record) => shopName || <Tag>Non assigné</Tag>,
      filters: shops.map(shop => ({ text: shop.name, value: shop.id })),
      onFilter: (value, record) => record.shop_id === value,
      sorter: (a, b) => (a.shop_name || '').localeCompare(b.shop_name || ''),
    },
    {
      title: 'Statut',
      dataIndex: 'is_active',
      key: 'is_active',
      filters: [
        { text: 'Actif', value: true },
        { text: 'Inactif', value: false },
      ],
      onFilter: (value, record) => record.is_active === value,
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'error'}>
          {isActive ? 'Actif' : 'Inactif'}
        </Tag>
      ),
    },
    {
      title: 'Créé le',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => formatDate(date),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      render: (_, record) => (
        <Popconfirm
          title={`Êtes-vous sûr de vouloir ${record.is_active ? 'désactiver' : 'activer'} ce caissier ?`}
          onConfirm={() => handleToggleActive(record.id, record.is_active)}
          okText="Oui"
          cancelText="Non"
          disabled={loading}
        >
          <Button
            type="primary"
            ghost={record.is_active}
            danger={record.is_active}
            icon={record.is_active ? <CloseOutlined /> : <CheckOutlined />}
            loading={loading}
          >
            {record.is_active ? 'Désactiver' : 'Activer'}
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const validatePinCode = (_, value) => {
    if (!value) {
      return Promise.reject('Le code PIN est requis');
    }
    if (!/^\d{6}$/.test(value)) {
      return Promise.reject('Le code PIN doit contenir exactement 6 chiffres');
    }
    return Promise.resolve();
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Gestion des Caissiers</Title>

      {error && (
        <Alert
          message="Erreur"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}
      {success && (
        <Alert
          message="Succès"
          description={success}
          type="success"
          showIcon
          closable
          onClose={() => setSuccess(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 24, marginTop: 16 }}>
        <Card title="Ajouter un caissier">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleAddCashier}
            autoComplete="off"
          >
            <Form.Item
              name="name"
              label="Nom du caissier"
              rules={[{ required: true, message: 'Le nom du caissier est requis' }]}
            >
              <Input placeholder="Nom du caissier" disabled={loading} />
            </Form.Item>

            <Form.Item
              name="pinCode"
              label="Code PIN (6 chiffres)"
              rules={[{ validator: validatePinCode }]}
            >
              <Input.Password
                placeholder="Code PIN à 6 chiffres"
                maxLength={6}
                disabled={loading}
              />
            </Form.Item>

            <Form.Item
              name="shopId"
              label="Boutique assignée"
              rules={[{ required: true, message: 'Veuillez sélectionner une boutique' }]}
            >
              <Select
                placeholder="Sélectionner une boutique"
                loading={loadingShops}
                disabled={loadingShops || loading}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {shops.map(shop => (
                  <Option key={shop.id} value={shop.id}>
                    {shop.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<PlusOutlined />}
                loading={loading}
                block
              >
                Ajouter le caissier
              </Button>
            </Form.Item>
          </Form>
        </Card>

        <Card title="Liste des caissiers">
          <Spin spinning={loading}>
            <Table
              columns={columns}
              dataSource={cashiers.map(c => ({...c, key: c.id}))}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: 'Aucun caissier enregistré' }}
              scroll={{ x: 'max-content' }}
            />
          </Spin>
        </Card>
      </div>
    </div>
  );
};

export default Cashiers; 